/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import includes from 'lodash.includes';
import {translate} from '@ion/core/src/locale/locale';
import {BulkSettingFieldValues, SCIField} from '@ion/core/src/types/entities';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {BulkSCISelectField} from './BulkSCISelectField';
import {BulkSCIStyleProps} from './BulkSCISettings';
import {getBulkSelectedValue, nonFlightModeTypes} from './SCISettingsUtils';

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    content : {
      padding : '30px 0 0 20px'
    },
    radioLabel : {
      ...dlsAlt('typography', 'label1'),
      color         : dlsColor('grey2'),
      fontSize      : '15px',
      paddingLeft   : '65%',
      wordSpacing   : '3px',
      letterSpacing : '0px'
    },
    customTabPaper : {
      marginTop : '20px',
      overflow  : 'auto',
      width     : '91%',
      boxShadow : 'none',
      display   : 'flex'
    },
    actionsTile : {
      display         : 'flex',
      justifyContent  : 'space-between',
      backgroundColor : 'white',
      padding         : 10,
      alignItems      : 'center'
    },
    overridesReportTitle : {
      ...dlsAlt('maps', 'layer', 'listHeader'),
      color      : dlsColor('grey1'),
      width      : '100%',
      fontWeight : 'normal'
    },
    noDataStyle : {
      padding : '18% 0 0 27%'
    },
    bulkRadioLabel : {
      ...dlsAlt('typography', 'label1'),
      color         : dlsColor('grey2'),
      fontSize      : '15px',
      paddingLeft   : '54%',
      paddingBottom : '10px',
      letterSpacing : '0px',
      wordSpacing   : '38px'
    },
    bulkActionsTile : {
      display         : 'flex',
      justifyContent  : 'space-between',
      backgroundColor : 'white',
      padding         : 10,
      alignItems      : 'center',
      marginTop       : '8px'
    }
  };
});

export interface BulkSCICustomTab extends BulkSCIStyleProps {
  selectedDevicetype: string;
  customTabFields: SCIField[];
  setCustomTabFields: (customFields: SCIField[]) => void;
}

/**
 * Edit Custom Control related settings for SCI Devices in bulk
 */
export function BulkSCICustomTab({
  customTabFields,
  selectedDevicetype,
  setCustomTabFields,
  isBulkAction = false
}: BulkSCICustomTab) {
  const classes = useStyles();

  const nonCustomFields = useCallback((item): boolean => {
    return (item.label === translate('admin-devices:view.sciSettings.flightMode') &&
    includes(nonFlightModeTypes, selectedDevicetype));
  }, []);

  const onRadioChange = useCallback((e, {label}, key) => {
    const initialControlData = customTabFields;
    initialControlData.find(switchItem => switchItem.label === label)[key] = getBulkSelectedValue(e?.target?.value);
    setCustomTabFields([...initialControlData]);
  }, []);

  const fields = useMemo(() => (
    `${BulkSettingFieldValues.on} ${isBulkAction ? BulkSettingFieldValues.bulkDefault : BulkSettingFieldValues.default} ${BulkSettingFieldValues.off}`
  ), []);

  return (
    <div className={classes.content}>
      {!!customTabFields?.length && (
        <div className={isBulkAction ? classes.bulkRadioLabel : classes.radioLabel}>
          {fields}
        </div>)}
      {customTabFields?.length ? customTabFields.map((item) => {
        if (!nonCustomFields(item)) {
          return (
            <div>
              {Object.entries(item).map(([key, value]) => {
                if (key !== 'label') {
                  return (
                    <div className={!isBulkAction && classes.customTabPaper}>
                      <FormItem fullWidth>
                        <div className={isBulkAction ? classes.bulkActionsTile : classes.actionsTile}>
                          <BodyTypography className={classes.overridesReportTitle}>
                            {item.label}
                          </BodyTypography>
                          <BulkSCISelectField value={value}
                            handleRadioChange={onRadioChange}
                            field={item}
                            selected={key}/>
                        </div>
                      </FormItem>
                    </div>
                  );
                }
              })}
            </div>
          );
        }
      }) : <div className={classes.noDataStyle}>{translate('admin-devices:view.sciSettings.noRowsMessage')}</div> }
    </div>
  );
};

export default BulkSCICustomTab;
